import type { AuthError, Session, User } from "@supabase/supabase-js";
import { useRouter } from "@tanstack/react-router";
import { usePostHog } from "posthog-js/react";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { addAuthActionBreadcrumb } from "../lib/sentryBreadcrumbs";
import { supabase } from "../lib/supabase";

interface AuthContextType {
    user: User | null;
    session: Session | null;
    loading: boolean;
    signUp: (email: string, password: string) => Promise<{ error: AuthError | null }>;
    signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
    signOut: () => Promise<{ error: AuthError | null }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState(true);
    const posthog = usePostHog();
    const router = useRouter();

    useEffect(() => {
        // Get initial session
        const getSession = async () => {
            const {
                data: { session },
            } = await supabase.auth.getSession();
            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);

            // Identify user with PostHog
            if (session?.user && posthog) {
                posthog.identify(session.user.id, {
                    email: session.user.email,
                    created_at: session.user.created_at,
                    email_confirmed: Boolean(session.user.email_confirmed_at),
                });
            }
        };

        getSession();

        // Listen for auth changes
        const {
            data: { subscription },
        } = supabase.auth.onAuthStateChange(async (event, session) => {
            setSession(session);
            setUser(session?.user ?? null);
            setLoading(false);

            // Handle session errors and token expiration
            if (event === "TOKEN_REFRESHED" && !session) {
                console.warn("Token refresh failed - session expired");
                // Force sign out to clean up stale tokens
                await supabase.auth.signOut({ scope: "local" });
            }

            // Handle successful sign in - redirect only from auth pages
            if (event === "SIGNED_IN" && session?.user) {
                const currentPath = window.location.pathname;
                if (currentPath === "/sign-in" || currentPath === "/sign-up") {
                    // Check for redirect parameter in URL
                    const urlParams = new URLSearchParams(window.location.search);
                    const redirectTo = urlParams.get("redirectTo");

                    const destination = redirectTo ? decodeURIComponent(redirectTo) : "/";
                    console.log(`User signed in, redirecting to: ${destination}`);

                    // Use router navigation - no page refresh needed
                    router.navigate({ to: destination });
                }
            }

            // Update PostHog user context
            if (session?.user && posthog) {
                posthog.identify(session.user.id, {
                    email: session.user.email,
                    created_at: session.user.created_at,
                    last_sign_in: session.user.last_sign_in_at,
                    email_confirmed: Boolean(session.user.email_confirmed_at),
                });
            } else if (posthog) {
                // Reset user context on sign out
                posthog.reset();
            }

            // Redirect to home page after successful sign out
            if (event === "SIGNED_OUT") {
                // Only redirect if not already on public pages
                const currentPath = window.location.pathname;
                const publicPages = ["/", "/sign-in", "/sign-up", "/help", "/terms", "/privacy", "/404"];

                if (!publicPages.includes(currentPath)) {
                    console.log("User signed out, redirecting to home");
                    router.navigate({ to: "/" });
                }
            }
        });

        return () => subscription.unsubscribe();
    }, [posthog, router]);

    const signUp = async (email: string, password: string) => {
        addAuthActionBreadcrumb("sign_up_attempt", { email: email.split("@")[1] }); // Only domain for privacy
        const { error } = await supabase.auth.signUp({ email, password });
        if (error) {
            addAuthActionBreadcrumb("sign_up_failed", { error: error.message });
        } else {
            addAuthActionBreadcrumb("sign_up_success");
        }
        return { error };
    };

    const signIn = async (email: string, password: string) => {
        addAuthActionBreadcrumb("sign_in_attempt", { email: email.split("@")[1] }); // Only domain for privacy
        const { error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) {
            addAuthActionBreadcrumb("sign_in_failed", { error: error.message });
        } else {
            addAuthActionBreadcrumb("sign_in_success");
        }
        return { error };
    };

    const signOut = async () => {
        addAuthActionBreadcrumb("sign_out_attempt");

        const forceLocalClearance = () => {
            setSession(null);
            setUser(null);
            if (posthog) {
                posthog.reset();
            }
        };

        try {
            const { error } = await supabase.auth.signOut({ scope: "local" });

            // If sign out failed due to session issues, force clear local state
            if (error) {
                const sessionErrors = ["session_not_found", "Auth session missing", "Invalid session", "expired"];

                if (sessionErrors.some((err) => error.message.includes(err))) {
                    console.warn("Session sign out failed, clearing local state:", error.message);
                    addAuthActionBreadcrumb("sign_out_success", { method: "force_local_clear" });
                    forceLocalClearance();
                    return { error: null };
                }
                addAuthActionBreadcrumb("sign_out_failed", { error: error.message });
            } else {
                addAuthActionBreadcrumb("sign_out_success");
            }

            return { error };
        } catch (error) {
            console.warn("Sign out failed, forcing local cleanup:", error);
            addAuthActionBreadcrumb("sign_out_success", { method: "force_local_clear_exception" });
            forceLocalClearance();
            return { error: null };
        }
    };

    const value = {
        user,
        session,
        loading,
        signUp,
        signIn,
        signOut,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
